import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, ClipboardList, Sparkles, RefreshCw } from 'lucide-react';
import { discussionsApi, tasksApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/lib/utils';

const priorityOptions = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'URGENT', label: 'Urgent' },
];

/**
 * Agency-admin modal: turn a discussion into a Hub task. Opens with an
 * AI-drafted title/description (or a plain excerpt when AI is off), all
 * editable before anything is created. The created task is linked back to
 * the discussion via sourceMessageId.
 */
export function CreateTaskFromDiscussionModal({
  isOpen,
  onClose,
  threadId,
  projectId,
}: {
  isOpen: boolean;
  onClose: () => void;
  threadId: string;
  projectId: string;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [listId, setListId] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [draftApplied, setDraftApplied] = useState(false);

  const { data: taskLists = [] } = useQuery({
    queryKey: ['taskLists', projectId],
    queryFn: () => tasksApi.listTaskLists(projectId),
    enabled: isOpen && !!projectId,
  });

  const {
    data: draft,
    isFetching: drafting,
    refetch: regenerate,
  } = useQuery({
    queryKey: ['discussion-ticket-draft', threadId],
    queryFn: () => discussionsApi.ticketDraft(threadId),
    enabled: isOpen && !!threadId,
    staleTime: Infinity,
    retry: false,
  });

  // Apply the draft once per open; edits after that are never clobbered
  // (except by an explicit Regenerate, which resets the flag).
  useEffect(() => {
    if (isOpen && draft && !draftApplied) {
      setTitle(draft.title);
      setDescription(draft.description);
      setDraftApplied(true);
    }
  }, [isOpen, draft, draftApplied]);

  // Default the list to the first available once lists load.
  useEffect(() => {
    if (isOpen && !listId && taskLists.length > 0) {
      setListId(taskLists[0].id);
    }
  }, [isOpen, listId, taskLists]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setListId('');
    setPriority('MEDIUM');
    setDraftApplied(false);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      discussionsApi.createTask(threadId, {
        listId,
        title: title.trim(),
        description,
        priority,
      }),
    onSuccess: (task) => {
      queryClient.invalidateQueries({ queryKey: ['message', threadId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      toast({
        type: 'success',
        title: 'Task created',
        message: `"${task.title}" is linked to this discussion.`,
      });
      resetForm();
      onClose();
    },
    onError: (e: any) => {
      toast({ type: 'error', title: "Couldn't create the task", message: e?.message });
    },
  });

  const handleClose = () => {
    if (!createMutation.isPending) {
      resetForm();
      onClose();
    }
  };

  const handleRegenerate = () => {
    setDraftApplied(false);
    regenerate();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !listId) return;
    createMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in" onClick={handleClose} />

      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent/10">
              <ClipboardList className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-heading-4 text-secondary">Create task from this discussion</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-6 space-y-5 overflow-y-auto flex-1">
            {drafting && !draftApplied ? (
              <div className="flex items-center gap-2 py-10 justify-center text-grey-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Drafting with AI…</span>
              </div>
            ) : (
              <>
                {/* Draft provenance + regenerate */}
                <div className="flex items-center justify-between text-xs text-grey-500">
                  {draft?.aiGenerated ? (
                    <span className="inline-flex items-center gap-1">
                      <Sparkles className="w-3.5 h-3.5 text-accent" />
                      Drafted by AI from the discussion — edit anything before creating.
                    </span>
                  ) : (
                    <span>AI unavailable — prefilled from the discussion. Edit before creating.</span>
                  )}
                  {draft?.aiGenerated && (
                    <button
                      type="button"
                      onClick={handleRegenerate}
                      disabled={drafting}
                      className="inline-flex items-center gap-1 text-accent hover:underline disabled:opacity-50"
                    >
                      <RefreshCw className={cn('w-3 h-3', drafting && 'animate-spin')} />
                      Regenerate
                    </button>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="task-title" className="block text-sm font-medium text-secondary mb-2">
                    Title <span className="text-error">*</span>
                  </label>
                  <input
                    id="task-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="input font-semibold"
                    required
                    disabled={createMutation.isPending}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">Description</label>
                  <RichTextEditor
                    content={description}
                    onChange={setDescription}
                    minHeight="160px"
                    disabled={createMutation.isPending}
                  />
                </div>

                {/* List + priority */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="task-list" className="block text-sm font-medium text-secondary mb-2">
                      Task list <span className="text-error">*</span>
                    </label>
                    <select
                      id="task-list"
                      value={listId}
                      onChange={(e) => setListId(e.target.value)}
                      className="input"
                      required
                      disabled={createMutation.isPending}
                    >
                      {taskLists.length === 0 && <option value="">No task lists in this project</option>}
                      {taskLists.map((list: any) => (
                        <option key={list.id} value={list.id}>
                          {list.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="task-priority" className="block text-sm font-medium text-secondary mb-2">
                      Priority
                    </label>
                    <select
                      id="task-priority"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="input"
                      disabled={createMutation.isPending}
                    >
                      {priorityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button type="button" variant="ghost" onClick={handleClose} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || !listId || createMutation.isPending || (drafting && !draftApplied)}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create Task'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
